import React, { useEffect, useState, useRef } from "react";
import { verifyEmail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Milk, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";

export default function VerifyEmail() {
  const { t } = useT();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against double-invoke in strict mode
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get("token") || "";
    if (!token) {
      setStatus("error");
      setMessage(t("verify.missing"));
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        const d = err.response?.data?.detail;
        setMessage(typeof d === "string" ? d : t("verify.failed"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center header-band px-4 py-10">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--brand))] text-white shadow-lg">
            <Milk className="h-7 w-7" />
          </div>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center p-8 text-center">
            {status === "loading" && (
              <div data-testid="verify-loading">
                <Loader2 className="h-10 w-10 animate-spin text-[hsl(var(--brand))]" />
                <p className="mt-4 text-sm text-muted-foreground">{t("verify.loading")}</p>
              </div>
            )}
            {status === "success" && (
              <div className="flex flex-col items-center" data-testid="verify-success">
                <CheckCircle2 className="h-12 w-12 text-[hsl(var(--brand))]" />
                <h2 className="mt-4 text-lg font-semibold">{t("verify.successTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t("verify.successBody")}</p>
                <Button className="mt-5 w-full" onClick={() => { window.location.href = "/login"; }} data-testid="verify-login-button">
                  {t("verify.goToLogin")}
                </Button>
              </div>
            )}
            {status === "error" && (
              <div className="flex flex-col items-center" data-testid="verify-error">
                <XCircle className="h-12 w-12 text-[hsl(var(--destructive))]" />
                <h2 className="mt-4 text-lg font-semibold">{t("verify.errorTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{message || t("verify.failed")}</p>
                <Button variant="outline" className="mt-5 w-full" onClick={() => { window.location.href = "/login"; }} data-testid="verify-login-button">
                  {t("verify.goToLogin")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
